import { useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReducedMotion } from 'framer-motion';
import { useTransitionConfig } from './CheckInTransitionContext.js';

export interface CloudRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function useCheckInTransition() {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [cloudRect, setCloudRect] = useState<CloudRect | null>(null);
  const cloudRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  const config = useTransitionConfig();

  const startTransition = useCallback(() => {
    // Reduced motion: skip animation, navigate instantly
    if (prefersReducedMotion) {
      navigate('/check-in', { replace: true });
      return;
    }

    // Capture cloud bounding rect at click time (handles any scroll position)
    const rect = cloudRef.current?.getBoundingClientRect();
    if (rect) {
      setCloudRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    }

    setIsTransitioning(true);
  }, [prefersReducedMotion, navigate]);

  const onTransitionComplete = useCallback(() => {
    navigate('/check-in/weather', { replace: true, state: { fromTransition: true } });
  }, [navigate]);

  const resetTransition = useCallback(() => {
    setIsTransitioning(false);
    setCloudRect(null);
  }, []);

  return {
    cloudRef,
    isTransitioning,
    cloudRect,
    config,
    startTransition,
    onTransitionComplete,
    resetTransition,
  };
}
